import React, { useMemo, useState } from "react";
import { Activity, Gauge, Rocket, Users } from "lucide-react";
import KickoffSprintView from "../../KickoffSprintView";
import KickoffTokensView from "../../KickoffTokensView";
import KickoffOwnerView from "../../KickoffOwnerView";

const TABS = [
  {
    id: "sprints",
    label: "Playbook sprints",
    helper: "Ordonnez les objectifs par vague",
  },
  {
    id: "tokens",
    label: "Efforts & tokens",
    helper: "Distribuez la vélocité",
  },
  {
    id: "owners",
    label: "Ownership & animation",
    helper: "Verrouillez les responsables",
  },
];

const KickoffPanel = ({
  projectId,
  project,
  objectives = [],
  iterations = [],
  participants = [],
  presenceCount = 0,
  realtimeConnected = false,
  refreshToken = 0,
  onKickoffSync,
}) => {
  const [activeTab, setActiveTab] = useState("sprints");
  const resolvedProjectId = useMemo(() => {
    if (projectId && !Number.isNaN(Number(projectId))) return Number(projectId);
    if (project?.id && !Number.isNaN(Number(project.id))) {
      return Number(project.id);
    }
    return null;
  }, [projectId, project]);

  const metrics = useMemo(() => {
    const totalObjectives = objectives.length;
    const plannedObjectives = objectives.filter(
      (objective) => !!objective?.sprint_id,
    ).length;
    const unplannedObjectives = Math.max(totalObjectives - plannedObjectives, 0);
    const effortTotals = objectives.reduce(
      (acc, objective) => {
        const effort = Number(objective?.effort) || 0;
        if (effort > 0) {
          return {
            totalEffort: acc.totalEffort + effort,
            countedObjectives: acc.countedObjectives + 1,
          };
        }
        return acc;
      },
      { totalEffort: 0, countedObjectives: 0 },
    );
    const avgToken =
      effortTotals.countedObjectives > 0
        ? (effortTotals.totalEffort / effortTotals.countedObjectives).toFixed(1)
        : "0";
    const sprintCount = iterations.reduce(
      (acc, iteration) => acc + (iteration?.sprints?.length || 0),
      0,
    );
    const ownerCount = participants.length;

    return [
      {
        id: "planning",
        label: "Objectifs planifiés",
        value: `${plannedObjectives}/${totalObjectives || 0}`,
        helper:
          totalObjectives === 0
            ? "Créez vos premiers objectifs"
            : unplannedObjectives === 0
              ? "Tout est planifié"
              : `${unplannedObjectives} restant${unplannedObjectives > 1 ? "s" : ""} à placer`,
        icon: TargetGlyph,
      },
      {
        id: "tokens",
        label: "Tokens estimés",
        value: `${effortTotals.totalEffort} pts`,
        helper:
          effortTotals.countedObjectives === 0
            ? "Aucune estimation"
            : `${avgToken} pts / objectif estimé`,
        icon: Activity,
      },
      {
        id: "owners",
        label: "Équipe kickoff",
        value: ownerCount > 0 ? ownerCount : "—",
        helper:
          sprintCount > 0
            ? `${sprintCount} sprint${sprintCount > 1 ? "s" : ""} prêts`
            : "Ajoutez vos sprints",
        icon: Users,
      },
    ];
  }, [iterations, objectives, participants]);

  const renderActiveArea = () => {
    if (!resolvedProjectId) {
      return (
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 text-center text-slate-500">
          <Rocket className="mb-3 h-10 w-10 text-slate-400" />
          <p className="text-base font-semibold">
            Sélectionnez un projet pour lancer le kickoff
          </p>
          <p className="text-sm">
            Nous n’avons pas réussi à identifier le projet cible.
          </p>
        </div>
      );
    }

    const commonProps = {
      projectId: resolvedProjectId,
      variant: "panel",
      refreshToken,
      onKickoffSync,
      participants,
    };
    if (activeTab === "tokens") {
      return (
        <div className="rounded-2xl border border-slate-100 bg-white shadow-inner">
          <KickoffTokensView {...commonProps} />
        </div>
      );
    }
    if (activeTab === "owners") {
      return (
        <div className="rounded-2xl border border-slate-100 bg-white shadow-inner">
          <KickoffOwnerView {...commonProps} />
        </div>
      );
    }
    return (
      <div className="rounded-2xl border border-slate-100 bg-white shadow-inner">
        <KickoffSprintView {...commonProps} />
      </div>
    );
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="relative overflow-hidden rounded-t-3xl bg-gradient-to-r from-slate-900 via-indigo-900 to-blue-700 px-6 py-8 text-white">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-white/70">
              Kickoff projet
            </p>
            <h2 className="mt-1 text-3xl font-semibold">
              {project?.name || "Projet en cours"}
            </h2>
            <p className="mt-1 text-sm text-white/80">
              Synchronisez sprint, estimation et ownership.
            </p>
          </div>
          <div className="inline-flex items-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-5 py-3 backdrop-blur">
            <Rocket className="h-5 w-5" />
            <div>
              <p className="text-xs uppercase tracking-wider text-white/80">
                Mode Kickoff
              </p>
              <p className="text-base font-semibold">
                {TABS.find((tab) => tab.id === activeTab)?.label}
              </p>
              <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-[10px] uppercase tracking-wider text-white/80">
                <span className={`h-2 w-2 rounded-full ${realtimeConnected ? "bg-emerald-400" : "bg-white/40"}`} />
                {realtimeConnected ? "Synchro en direct" : "Hors ligne"}
                {presenceCount > 0 && <span className="opacity-70">({presenceCount})</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div
                key={metric.id}
                className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white backdrop-blur"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/70">
                      {metric.label}
                    </p>
                    <p className="text-xl font-semibold">{metric.value}</p>
                  </div>
                </div>
                <p className="mt-2 text-xs text-white/80">{metric.helper}</p>
              </div>
            );
          })}
        </div>
        <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full border border-white/20 opacity-30" />
      </div>
      <div className="border-b border-slate-100 bg-slate-50/60 px-4 py-3">
        <div className="flex flex-col gap-3 md:flex-row">
          {TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 rounded-2xl border px-4 py-3 text-left transition ${isActive ? "border-blue-200 bg-white shadow-sm" : "border-transparent bg-transparent hover:border-slate-200 hover:bg-white/80"}`}
              >
                <p
                  className={`text-sm font-semibold ${isActive ? "text-slate-900" : "text-slate-600"}`}
                >
                  {tab.label}
                </p>
                <p className="text-xs text-slate-500">{tab.helper}</p>
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-4 lg:p-6">{renderActiveArea()}</div>
    </div>
  );
};

const TargetGlyph = (props) => <Gauge {...props} />;

export default KickoffPanel;
